import { TextInput, TextInputProps } from 'grommet';
import { ChangeEvent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { countSig, formatCurrency, indexOfSig, parseCurrency, sanitizeNumeric } from 'src/utils/currency';

export interface ICurrencyInputProps extends Omit<TextInputProps, 'onChange'> {
  /** true (default) -> 2-decimal ATM-style entry, onChange emits a float.
   *  false          -> whole-dollar comma formatting, onChange emits an integer. */
  currencyDecimals?: boolean;
  onChange?: ((value: number) => void) | TextInputProps['onChange'];
}

/**
 * Thin pass-through wrapper around Grommet's `TextInput`. Adds USD currency formatting.
 * All other Grommet props (placeholder, disabled, readOnly, autoComplete, aria-*, style,
 * size, width, etc.) are forwarded unchanged.
 */
export const CurrencyInput = ({
  currencyDecimals = true,
  value: val,
  onChange,
  ...textInputProps
}: ICurrencyInputProps) => {
  /** State **/
  const [value, setValue] = useState<string | number>(
    val !== undefined && val !== null ? formatCurrency(Number(val), currencyDecimals) : ''
  );
  // Caret position to restore after currency reformatting (currency edit mode only).
  const [selection, setSelection] = useState<[number, number] | null>(null);

  /** Refs **/
  const inputRef = useRef<HTMLInputElement>(null);

  /** Methods **/
  const numericOnChange = onChange as ((value: number) => void) | undefined;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (currencyDecimals) {
      // ATM-style fixed-decimal entry: digits shift in from the right of the fixed decimal.
      const prevDisplay = String(value);
      const oldCents = Math.round((parseCurrency(prevDisplay) || 0) * 100);
      const oldDigits = prevDisplay.replace(/[^0-9]/g, '');
      const newStr = e.target.value;
      const newDigits = newStr.replace(/[^0-9]/g, '');

      let cents: number;
      if (newDigits.length === oldDigits.length + 1 && newDigits.startsWith(oldDigits)) {
        // A digit was appended at the end -> shift left by one decimal place.
        const d = parseInt(newDigits.slice(-1), 10);
        cents = oldCents * 10 + d;
      } else if (oldDigits.length === newDigits.length + 1 && oldDigits.startsWith(newDigits)) {
        // A digit was removed (backspace) -> shift right by one decimal place.
        cents = Math.floor(oldCents / 10);
      } else {
        // Paste / select-replace / clear -> reparse the whole string, defaulting to 0.
        cents = Math.round((parseCurrency(newStr) || 0) * 100);
      }

      const formatted = formatCurrency(cents / 100, true);
      setValue(formatted);
      setSelection([formatted.length, formatted.length]);
      numericOnChange?.(cents / 100);
      return;
    }

    // Whole-dollar mode: live comma formatting with caret preservation.
    const caret = e.target.selectionStart ?? e.target.value.length;
    const sigBefore = countSig(e.target.value.slice(0, caret));
    const raw = sanitizeNumeric(e.target.value);
    const formatted = formatCurrency(parseCurrency(raw) || 0, false);
    const newCaret = indexOfSig(formatted, sigBefore);
    setValue(formatted);
    setSelection([newCaret, newCaret]);
    numericOnChange?.(parseCurrency(raw) || 0);
    return;
  };

  /** Effects **/

  // Restore caret after currency reformatting renders the new value.
  useLayoutEffect(() => {
    if (selection && inputRef.current) {
      inputRef.current.selectionStart = selection[0];
      inputRef.current.selectionEnd = selection[1];
    }
  }, [selection, value]);

  // Re-sync the displayed value when the controlled `value` prop changes from outside.
  useEffect(() => {
    if (val === undefined || val === null) return;
    setValue(formatCurrency(Number(val), currencyDecimals));
  }, [val, currencyDecimals]);

  /** Render **/
  return <TextInput ref={inputRef} value={value} onChange={handleChange} type="text" plain {...textInputProps} />;
};
